import Link from "@/components/ui/RouterLink";
import { SectionEyebrow } from "./SectionEyebrow";
import type { ReactNode } from "react";

type Cta = { label: string; href: string; external?: boolean; icon?: ReactNode };

export function PageHero({
  eyebrow,
  title,
  description,
  primaryCta,
  secondaryCta,
  visual,
}: {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta?: Cta;
  secondaryCta?: Cta;
  visual?: ReactNode;
}) {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="grid-backdrop absolute inset-0 opacity-30" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <div className={visual ? "grid grid-cols-1 items-center gap-12 lg:grid-cols-2" : "max-w-2xl"}>
          <div>
            <SectionEyebrow>{eyebrow}</SectionEyebrow>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-tight text-text-primary sm:text-5xl">
              {title}
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-text-secondary">
              {description}
            </p>
            {(primaryCta || secondaryCta) && (
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                {primaryCta &&
                  (primaryCta.external ? (
                    <a
                      href={primaryCta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110"
                    >
                      {primaryCta.label} {primaryCta.icon}
                    </a>
                  ) : (
                    <Link
                      href={primaryCta.href}
                      className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110"
                    >
                      {primaryCta.label} {primaryCta.icon}
                    </Link>
                  ))}
                {secondaryCta &&
                  (secondaryCta.external ? (
                    <a
                      href={secondaryCta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-medium text-text-primary transition hover:border-cyan/50"
                    >
                      {secondaryCta.icon} {secondaryCta.label}
                    </a>
                  ) : (
                    <Link
                      href={secondaryCta.href}
                      className="focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-medium text-text-primary transition hover:border-cyan/50"
                    >
                      {secondaryCta.icon} {secondaryCta.label}
                    </Link>
                  ))}
              </div>
            )}
          </div>
          {visual}
        </div>
      </div>
    </section>
  );
}
